/* eslint-disable no-console */
import atmosphere from 'atmosphere.js'
import { getAppInstanceId } from 'config/appInstance'
import { AtmosphereTransport, AtmosphereTransportManager } from 'hooks/atmosphere/AtmosphereTransportManager'
import { RateLimiter } from 'util/RateLimiter'

// ===== CONFIGURATION TYPES =====

type ActionType = 'SUBSCRIBE' | 'UNSUBSCRIBE'
type LogLevel = 'debug' | 'info' | 'error'

const PEC_CLIENT_ID_HEADER = 'PEC-Client-ID'

interface PubSubConfig {
  baseUrl: string
  logLevel: LogLevel
  reconnectInterval: number
  pollingInterval: number
  trackMessageLength: boolean
  contentType: string
  maxReconnectAttempts: number
  headers: Record<string, string>
}

interface TopicMessage {
  topic: string
  data: any
  createdAt: number
}

interface SubscriptionMessage {
  action: ActionType
  topics: string[]
}

export class PubSubManager {
  private static instance: PubSubManager
  private readonly transportManager: AtmosphereTransportManager = AtmosphereTransportManager.getInstance()
  private socket: any = null
  private isConnected: boolean = false
  private currentTransport: AtmosphereTransport = this.transportManager.getCurrentTransport()
  private readonly subscribedTopics: Set<string> = new Set()
  private readonly topicHandlers: Map<string, Function> = new Map()
  private readonly config: PubSubConfig
  private readonly rateLimiter: RateLimiter = new RateLimiter({ windowMs: 1000, maxEvents: 3 })
  private reconnectAttempts: number = 0

  private constructor() {
    // Default configuration
    this.config = {
      baseUrl: `${window.location.protocol}//${window.location.host}/atmosphere/pec/pubsub`,
      logLevel: process.env.NODE_ENV === 'development' ? 'info' : 'error',
      reconnectInterval: 200,
      pollingInterval: 100,
      trackMessageLength: true,
      contentType: 'application/json; charset=UTF-8',
      maxReconnectAttempts: 10,
      headers: {
        [PEC_CLIENT_ID_HEADER]: getAppInstanceId(),
      },
    }
  }

  static getInstance(): PubSubManager {
    if (!PubSubManager.instance) {
      PubSubManager.instance = new PubSubManager()
      PubSubManager.instance.connect()
    }
    return PubSubManager.instance
  }

  static destroy(): void {
    if (PubSubManager.instance) {
      PubSubManager.instance.disconnect()
      PubSubManager.instance = null as any
    }
  }

  private handleOnLoop() {
    this.reconnectAttempts++
    if (this.reconnectAttempts > 2) {
      this.disconnect()
    } else {
      this.connect()
    }
  }

  // ===== CONNECTION MANAGEMENT =====

  connect(): void {
    if (this.socket) return // Already connected

    const atmosphereConfig = {
      url: this.config.baseUrl,
      transport: this.currentTransport,
      fallbackTransport: undefined,
      logLevel: this.config.logLevel,
      contentType: this.config.contentType,
      trackMessageLength: this.config.trackMessageLength,
      reconnectInterval: this.config.reconnectInterval,
      pollingInterval: this.config.pollingInterval,
      enableProtocol: this.currentTransport === 'websocket',
      headers: this.config.headers,

      onOpen: () => {
        this.isConnected = true
        this.resubscribeTopics()
      },

      onClose: () => {
        this.isConnected = false
      },

      onReconnect: () => {
        if (this.rateLimiter.check()) {
          this.handleOnLoop()
        }
        this.isConnected = true
        this.resubscribeTopics()
      },

      onError: () => {
        this.isConnected = false
      },

      onReopen: () => {
        if (this.rateLimiter.check()) {
          this.handleOnLoop()
        }
        this.isConnected = true
        this.resubscribeTopics()
      },

      onFailureToReconnect: () => {
        this.isConnected = false
      },

      onClientTimeout: () => {
        this.isConnected = false
      },

      onTransportFailure: () => {
        this.isConnected = false
        this.handleTransportFailure()
      },

      onMessage: (response) => {
        this.handleMessage(response)
      },
    }
    this.socket = atmosphere.subscribe(atmosphereConfig)
  }

  disconnect(): void {
    this.isConnected = false
    if (this.socket) {
      atmosphere.unsubscribeUrl(this.config.baseUrl)
      this.socket = null
    }
  }

  // ===== TOPIC MANAGEMENT =====

  subscribeTopic(topic: string, handler: Function): void {
    this.topicHandlers.set(topic, handler)

    if (!this.subscribedTopics.has(topic)) {
      this.subscribedTopics.add(topic)
      this.sendSubscription({
        action: 'SUBSCRIBE',
        topics: [topic],
      })
    }
  }

  unsubscribeTopic(topic: string): void {
    this.topicHandlers.delete(topic)

    if (this.subscribedTopics.has(topic)) {
      this.subscribedTopics.delete(topic)
      this.sendSubscription({
        action: 'UNSUBSCRIBE',
        topics: [topic],
      })
    }
  }

  pushMessage<T>(message: T): void {
    if (this.isConnected && this.socket) {
      this.socket.push(JSON.stringify(message))
    } else {
      console.warn('Atmosphere - Attempted to push message while not connected.')
    }
  }

  // ===== PRIVATE METHODS =====

  private handleMessage(response): void {
    if (!this.isConnected || !response.responseBody) return

    try {
      const message: TopicMessage = JSON.parse(response.responseBody)
      const handler = this.topicHandlers.get(message.topic)

      if (handler) {
        handler(message.data, message.topic, message.createdAt)
      }
    } catch (error) {
      console.error('Error parsing message:', error)
    }
  }

  private sendSubscription(subscription: SubscriptionMessage): void {
    if (this.isConnected && this.socket) {
      this.socket.push(JSON.stringify(subscription))
    }
  }

  private resubscribeTopics(): void {
    if (this.subscribedTopics.size > 0) {
      const topics = Array.from(this.subscribedTopics)
      this.sendSubscription({
        action: 'SUBSCRIBE',
        topics,
      })
    }
  }

  private handleTransportFailure(): void {
    if (this.currentTransport === 'websocket') {
      this.transportManager.setCurrentTransport('long-polling')
      this.currentTransport = 'long-polling'
      this.disconnect()
      setTimeout(() => this.connect(), 100)
    } else {
      this.disconnect()
      console.error('Atmosphere transport failed - not able to connect with long-polling and websocket')
    }
  }
}
